import React, { useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { RESOURCE_NAME } from "common/authorization";
import { OBJECT_TYPE, WORKFLOW_STEP_ROLE } from "common/constants";
import { buildLink } from "common/routing";
import { GetWorkflowQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexRow } from "../Common/__styles__/Layout";
import { Button } from "../Common/Buttons/Button";
import { Icon } from "../Common/Icons/LucideIcons";
import { Tag } from "../Common/Tag";
import { WorkflowStepContainer } from "./__styles__/WorkflowStep";

export const WorkflowStep = ({
  workflowStep,
  objectId,
  objectType,
}: {
  workflowStep: Omit<
    NonNullable<GetWorkflowQuery["workflow"]>["workflowSteps"][number],
    "status"
  >;
  objectId?: string;
  objectType?: OBJECT_TYPE;
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { isPublic, authorized } = useContext(AuthContext);

  let onCallToActionClick: (() => void) | undefined;

  const canCreateSubmission =
    isPublic ?
      workflowStep.workflowStepUser?.stepRole === WORKFLOW_STEP_ROLE.CREATE
    : authorized({
        permission: "create",
        resource: RESOURCE_NAME.SUBMISSION,
      });

  if (canCreateSubmission && !workflowStep.submission) {
    onCallToActionClick = () =>
      history.push({
        pathname: buildLink("createSubmission", {
          submissionTypeId:
            workflowStep.workflowStepDefinition.submissionType.id,
          objectId: objectId ?? undefined,
          objectType: objectType ?? undefined,
        }),
        state: { prevLocation: pathname, workflowStepId: workflowStep.id },
      });
  } else if (workflowStep.submission) {
    onCallToActionClick = () => {
      history.push({
        pathname: buildLink("editSubmission", {
          submissionId: workflowStep.submission!.id,
        }),
        state: { prevLocation: pathname },
      });
    };
  }

  let ctaText: string;
  switch (workflowStep.workflowStepUser?.stepRole) {
    case WORKFLOW_STEP_ROLE.CREATE:
      ctaText = workflowStep.submission ? "View" : "Create";
      break;
    case WORKFLOW_STEP_ROLE.UPDATE:
      ctaText = "Review";
      break;
    case WORKFLOW_STEP_ROLE.VIEW:
      ctaText = "View";
      break;
    case undefined:
      if (canCreateSubmission && !workflowStep.submission) {
        ctaText = "Create";
      } else {
        ctaText = "View";
      }
      break;
  }

  return (
    <WorkflowStepContainer>
      <FlexRow style={{ gap: spacing.s.value, alignItems: "center" }}>
        <Icon
          iconName="file-text"
          color="contentSecondary"
          size={15}
          style={{
            flexShrink: 0,
          }}
        />
        {workflowStep.workflowStepDefinition.submissionType.name}
      </FlexRow>
      <FlexRow style={{ gap: spacing.l.value, alignItems: "center" }}>
        {workflowStep.workflowStepUser?.tag && (
          <Tag styleVariant="default" data-testid="workflow-step-tag">
            {workflowStep.workflowStepUser?.tag}
          </Tag>
        )}
        <Button
          variant="outline"
          size="small"
          onPress={onCallToActionClick}
          isDisabled={!onCallToActionClick}
          data-testid="workflow-step-cta"
          title={ctaText}
        />
      </FlexRow>
    </WorkflowStepContainer>
  );
};
